import cn from 'classnames';
import { useSpring, animated, config } from 'react-spring';
import { useRef, useState, memo, useMemo, useLayoutEffect } from 'react';
import SwiperCore, { Navigation } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import { IBlackjackGame, IBlackjackRoom } from '@api/types/blackjack';
import { ReactComponent as ChevronIcon } from '@assets/svg/chevron.svg';
import { getRoomGames } from '@utils/blackjack';
import GameItem from './item';
import useReduxState from '@hooks/useReduxState';
import styles from './room-games.module.scss';

SwiperCore.use([Navigation]);

const SLIDE_ITEM_OFFSET = 25;
const SLIDE_ITEM_WIDTH = 125 + SLIDE_ITEM_OFFSET;
const REMOVED_SLIDES_PER_VIEW = 1.15;

export interface IRoomGamesProps {
    room: IBlackjackRoom;
    currentGameUid?: string;
    className?: string;
}

const RoomGames = ({
    room,
    currentGameUid,
    className = '',
}: IRoomGamesProps): JSX.Element => {
    const [{ chat: chatState, ui }] = useReduxState((state) => state);
    const [slidesPerView, setSlidesPerView] = useState<number>(1);
    const swiperRef = useRef<HTMLDivElement>(null);

    const games: IBlackjackGame[] = useMemo(
        () => getRoomGames(room, currentGameUid),
        [room],
    );

    useLayoutEffect(() => {
        setSlidesPerView(handleSlidesPerView());
    }, [ui.windowWidth, chatState.collapsed]);

    /* Animations */

    const gamesSpring = useSpring({
        from: { opacity: 0, scale: 0 },
        to: { opacity: 1, scale: 1 },
        delay: 1000,
        config: config.slow,
    });

    /* Handlers */

    const handleSlidesPerView = (): number => {
        if (!swiperRef.current) return 1;

        const slides: number = Math.round(
            swiperRef.current!.clientWidth / SLIDE_ITEM_WIDTH,
        );

        return chatState.collapsed ? slides : slides - REMOVED_SLIDES_PER_VIEW;
    };

    const slidePrev = (): void => {
        if (!swiperRef.current) return;
        //@ts-ignore
        swiperRef.current.swiper.slidePrev();
    };

    const slideNext = (): void => {
        if (!swiperRef.current) return;
        //@ts-ignore
        swiperRef.current.swiper.slideNext();
    };

    /* Components */

    const listElements: JSX.Element[] = games.map((data) => (
        <SwiperSlide key={data.uid}>
            <GameItem data={data} />
        </SwiperSlide>
    ));

    /* ClassNames */

    const swiperClasses = cn(styles.games__list, {
        [styles.chatVisible]: !chatState.collapsed,
        [styles.notLobby]: currentGameUid,
    });

    return (
        <animated.section
            style={gamesSpring}
            className={cn(styles.games, className)}
        >
            <button className={styles.games__prevBtn} onClick={slidePrev}>
                <ChevronIcon />
            </button>

            <Swiper
                // @ts-ignore
                ref={swiperRef}
                slidesPerView={slidesPerView}
                className={swiperClasses}
            >
                {listElements}
            </Swiper>

            <button className={styles.games__nextBtn} onClick={slideNext}>
                <ChevronIcon />
            </button>
        </animated.section>
    );
};

export default memo(RoomGames);
